/**
 * loggerInitializer.js - Initializes the logger system for FuseSoul
 * 
 * This utility automatically loads logger configuration from the server
 * and applies it to control all console logs across the application.
 */

import { initLogger } from './logger';
import { hasLocalStorage, safeGetItem } from './storageSafe';
import axios from 'axios';
import { API_BASE_URL } from './apiBase';

// Create a public client for non-authenticated requests
const publicClient = axios.create({ 
  baseURL: API_BASE_URL,
  withCredentials: false 
});

// Note: localOverride is now stored in DB, not localStorage
// Keeping LOCAL_FLAG for backward compatibility with clearLocalStoragePreservingDebug
const LOCAL_FLAG = 'personalDebug';   // legacy key (no longer used for logger override)

// Helper function to clear localStorage while preserving personalDebug flag
export function clearLocalStoragePreservingDebug() {
  if (!hasLocalStorage()) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LoggerInit] clearLocalStoragePreservingDebug: localStorage unavailable, skipping');
    }
    return;
  }
  
  const keepDebug = safeGetItem(LOCAL_FLAG);
  if (process.env.NODE_ENV !== 'production') {
    console.log('[LoggerInit] clearLocalStoragePreservingDebug: Before clear, personalDebug =', keepDebug);
  }
  localStorage.clear();
  if (keepDebug) {
    localStorage.setItem(LOCAL_FLAG, keepDebug);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LoggerInit] clearLocalStoragePreservingDebug: Restored personalDebug =', keepDebug);
    }
  }
}

// Monitor localStorage changes to track when personalDebug gets wiped
export function monitorPersonalDebugFlag() {
  if (!hasLocalStorage()) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LoggerInit] monitorPersonalDebugFlag: localStorage unavailable, skipping');
    }
    return;
  }
  
  const originalSetItem = localStorage.setItem;
  const originalRemoveItem = localStorage.removeItem;
  const originalClear = localStorage.clear;
  
  localStorage.setItem = function(key, value) {
    if (key === LOCAL_FLAG) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LoggerInit] personalDebug SET to:', value);
      }
    }
    return originalSetItem.apply(this, arguments);
  };
  
  localStorage.removeItem = function(key) {
    if (key === LOCAL_FLAG) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LoggerInit] personalDebug REMOVED by removeItem');
        console.trace('[LoggerInit] Stack trace for personalDebug removal:');
      }
    }
    return originalRemoveItem.apply(this, arguments);
  };
  
  localStorage.clear = function() {
    const hadDebug = safeGetItem(LOCAL_FLAG);
    if (hadDebug) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[LoggerInit] personalDebug will be CLEARED by clear()');
        console.trace('[LoggerInit] Stack trace for localStorage.clear():');
      }
    }
    return originalClear.apply(this, arguments);
  };
}

export default async function initializeLogger() {
  // Start monitoring localStorage changes to track when personalDebug gets wiped
  if (hasLocalStorage()) {
    monitorPersonalDebugFlag();
  }
  
  let finalConfig = { enabled: false, level: 'error', overrideConsole: true }; // Default fallback
  let localOverride = false;
  let fetchedConfig = null;

  try {
    // 1. Fetch global config
    try {
      const { data } = await publicClient.get('/logger-config');
      fetchedConfig = data;
    } catch (fetchError) {
      // Continue with fallback config
    }
    
    // 2. Check for local override from DB (stored in localOverride field)
    if (fetchedConfig && typeof fetchedConfig.localOverride === 'boolean') {
      localOverride = fetchedConfig.localOverride;
    } else {
      localOverride = false;
    }
    
    // 3. Determine final configuration using priority rule
    finalConfig = {
      enabled: localOverride ? true : (fetchedConfig?.enabled ?? false),
      level: localOverride ? 'debug' : (fetchedConfig?.level ?? 'info'),
      overrideConsole: true
    };
    
    // 4. Initialize the logger
    try {
      initLogger(finalConfig);
    } catch (err) {
      // Silently continue if initLogger fails
    }
    
    // NOTE: initLogger() handles the logger configuration
    // No need to override console methods here
    
    return true;
    
  } catch (e) {
    // Fallback if API fetch fails - use default config
    localOverride = false;
    finalConfig = {
        enabled: localOverride ? true : false, // Default disabled if API fails
        level: localOverride ? 'debug' : 'error', // Default error level if API fails
        overrideConsole: true
    };
    
    try {
      initLogger(finalConfig);
    } catch (err) {
      // Silently continue if initLogger fails
    }
    
    // NOTE: initLogger() handles the logger configuration
    // No need to override console methods here
    
    return false;
  }
}
